import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, MapPin, MessageSquare, Calendar, Loader2, DollarSign } from 'lucide-react';
import { fetchEnquiryDetails } from '../api/analyticsApi';
import { cleanLeadMessageText } from '../../../helper/function';

const formatAgentNotes = (agentNotes) => {
    if (!agentNotes?.trim()) return [];
    return agentNotes.split(/\n\n+/).filter(Boolean).map((noteText) => {
        const match = noteText.match(/^\[([^\]]+)\]\s*(?:\(([^)]+)\))?\s*(.*)$/s);
        return {
            timestamp: match ? match[1] : '',
            author: match ? (match[2] || 'Agent') : 'Agent',
            content: match ? match[3].trim() : noteText.trim(),
        };
    });
};

const EnquiryDetailModal = ({ isOpen, onClose, startDate, endDate }) => {
    const [loading, setLoading] = useState(false);
    const [enquiries, setEnquiries] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadEnquiries();
        }
    }, [isOpen, startDate, endDate]);

    const loadEnquiries = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchEnquiryDetails(startDate, endDate);
            setEnquiries(data);
        } catch (err) {
            console.error('Error loading enquiries:', err);
            setError('Failed to load enquiries');
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatPrice = (price) => {
        if (!price) return null;
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            maximumFractionDigits: 0,
        }).format(price);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Enquiry Details</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : enquiries.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">No enquiries found</div>
                    ) : (
                        <div className="space-y-4">
                            {enquiries.map((enquiry) => (
                                <div
                                    key={enquiry.id}
                                    className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Left Column - Contact Info */}
                                        <div className="space-y-4">
                                            {/* Contact Name */}
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <User className="w-5 h-5 text-blue-600" />
                                                    <h3 className="font-bold text-gray-900 text-lg">{enquiry.name}</h3>
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    <Calendar className="w-3 h-3 inline mr-1" />
                                                    {formatTimestamp(enquiry.created_at)}
                                                </div>
                                            </div>

                                            {/* Contact Details */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Mail className="w-4 h-4 text-gray-400" />
                                                    <a href={`mailto:${enquiry.email}`} className="text-blue-600 hover:underline">
                                                        {enquiry.email}
                                                    </a>
                                                </div>
                                                {enquiry.phone && (
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Phone className="w-4 h-4 text-gray-400" />
                                                        <a href={`tel:${enquiry.phone}`} className="text-blue-600 hover:underline">
                                                            {enquiry.phone}
                                                        </a>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Buyer Info */}
                                            {enquiry.buyer && (
                                                <div className="bg-blue-50 rounded-lg p-3">
                                                    <div className="text-xs text-gray-600 mb-1">Registered Buyer</div>
                                                    <div className="text-sm font-semibold text-blue-900">
                                                        {enquiry.buyer.name}
                                                    </div>
                                                    <div className="text-xs text-blue-700">{enquiry.buyer.email}</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Right Column - Property & Message */}
                                        <div className="space-y-4">
                                            {/* Property Info */}
                                            {enquiry.property && (
                                                <div className="bg-gray-50 rounded-lg p-3">
                                                    <div className="text-xs text-gray-600 mb-2">Property of Interest</div>
                                                    <div className="flex items-start gap-2">
                                                        <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                                                        <div className="flex-1">
                                                            <div className="font-semibold text-gray-900">
                                                                {enquiry.property.title || enquiry.property.address}
                                                            </div>
                                                            {enquiry.property.suburb && (
                                                                <div className="text-sm text-gray-600">
                                                                    {enquiry.property.suburb}{enquiry.property.state && `, ${enquiry.property.state}`}
                                                                </div>
                                                            )}
                                                            {enquiry.property.price && (
                                                                <div className="flex items-center gap-1 mt-1">
                                                                    <DollarSign className="w-4 h-4 text-green-600" />
                                                                    <span className="text-sm font-semibold text-green-600">
                                                                        {formatPrice(enquiry.property.price)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {enquiry.property.method_of_sale && (
                                                                <span className="inline-block mt-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                                                    {enquiry.property.method_of_sale}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Buyer Message */}
                                            {(() => {
                                                const buyerMessage = cleanLeadMessageText(enquiry.message);
                                                const agentNotes = formatAgentNotes(enquiry.agent_notes);
                                                const hasBuyerMessage = buyerMessage && buyerMessage !== 'No comments';
                                                return (
                                                    <>
                                                        {hasBuyerMessage && (
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <MessageSquare className="w-4 h-4 text-gray-400" />
                                                                    <span className="text-sm font-semibold text-gray-700">Buyer Message</span>
                                                                </div>
                                                                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                                                    {buyerMessage}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {agentNotes.length > 0 && (
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <MessageSquare className="w-4 h-4 text-amber-500" />
                                                                    <span className="text-sm font-semibold text-gray-700">Agent Notes</span>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    {agentNotes.map((note, idx) => (
                                                                        <div key={idx} className="bg-amber-50 rounded-lg p-3 text-sm text-amber-900 whitespace-pre-wrap border border-amber-100">
                                                                            {note.timestamp && (
                                                                                <div className="text-xs text-amber-700 mb-1">
                                                                                    {note.timestamp}{note.author ? ` · ${note.author}` : ''}
                                                                                </div>
                                                                            )}
                                                                            {note.content}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}

                                            {/* Status */}
                                            {enquiry.status && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-600">Status:</span>
                                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                        enquiry.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                        enquiry.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-gray-100 text-gray-700'
                                                    }`}>
                                                        {enquiry.status}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                        Showing {enquiries.length} enquir{enquiries.length !== 1 ? 'ies' : 'y'}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EnquiryDetailModal;
